import type {ReactElement} from 'react';
import {useState} from 'react';
import React, {useEffect} from 'react';
import {Checkbox, CHECKBOX_TYPE} from '@Components/checkbox';
import {Text, TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {WeekDaySelectorsProps} from '@Components/event-wizard/week-day-selectors/week-day-selectors.types';
import {ALL_WEEKDAYS_USA, Weekdays} from '@Components/event-wizard/week-day-selectors/week-day-selectors.types';
import {useDispatch} from 'react-redux';
import {onEventWizardRecurringWeeklyChange} from '@Components/event-wizard/event-wizard-slice';
import {
  useEventWizardEndDateTime,
  useEventWizardRecurringData,
  useEventWizardRecurringWeeks,
  useEventWizardStartDateTime,
  useEventWizardTimeZone,
} from '@Components/event-wizard/event-wizard.hooks';
import {updatePreviewDateTime} from '@Components/event-wizard/preview/preview.helper';
import styles from './week-day-selectors.module.scss';

export function WeekDaySelectors({className}: WeekDaySelectorsProps): ReactElement<any> {
  const selectedStartDateTime = useEventWizardStartDateTime();
  const selectedEndDateTime = useEventWizardEndDateTime();
  const recurringPattern = useEventWizardRecurringData();
  const selectedTimezone = useEventWizardTimeZone();
  const dispatch = useDispatch();

  const [weekdaysSelected, setWeekdaysSelected] = useState<Weekdays[]>(useEventWizardRecurringWeeks());

  useEffect(() => {
    if (weekdaysSelected.length === 0) {
      dispatch(onEventWizardRecurringWeeklyChange([ALL_WEEKDAYS_USA[selectedStartDateTime.getDay()]]));
    }
  }, []);

  useEffect(() => {
    toggleSelectedDay(ALL_WEEKDAYS_USA[selectedStartDateTime.getDay()]);
  }, [selectedStartDateTime]);

  const toggleSelectedDay = (weekday: Weekdays): void => {
    const isWeekdaySelected = weekdaysSelected.includes(weekday);
    let updatedWeekdaysSelected;

    if (isWeekdaySelected) {
      if (!canRemoveSelectedWeekday(weekday)) {
        return;
      }
      updatedWeekdaysSelected = weekdaysSelected.filter((item) => {
        return item !== weekday;
      });
    } else {
      updatedWeekdaysSelected = [...weekdaysSelected, weekday];
    }
    setWeekdaysSelected(updatedWeekdaysSelected);

    dispatch(onEventWizardRecurringWeeklyChange(updatedWeekdaysSelected));
    updatePreviewDateTime({
      startDateTime: selectedStartDateTime,
      endDateTime: selectedEndDateTime,
      recurringPattern: {
        ...recurringPattern,
        recurringWeeks: updatedWeekdaysSelected,
      },
      timeZone: selectedTimezone,
    });
  };

  const getIsWeekdaySelected = (weekday: Weekdays): boolean => {
    return weekdaysSelected.includes(weekday);
  };

  const canRemoveSelectedWeekday = (weekday: Weekdays): boolean => {
    if (ALL_WEEKDAYS_USA[selectedStartDateTime.getDay()] === weekday) {
      return false;
    }
    return weekdaysSelected.length > 1;
  };

  const getDaySelector = (weekday: Weekdays): ReactElement<any> => {
    const isSelected: boolean = getIsWeekdaySelected(weekday);
    let checkBoxType: CHECKBOX_TYPE = CHECKBOX_TYPE.DEFAULT;

    if (isSelected) {
      checkBoxType = canRemoveSelectedWeekday(weekday) ? CHECKBOX_TYPE.PRIMARY : CHECKBOX_TYPE.LIGHT;
    }

    return (
      <div className="flex-column-align-center justify-content-center">
        <Checkbox
          onClick={() => {
            toggleSelectedDay(weekday);
          }}
          key={weekday}
          type={checkBoxType}
          disabled={!canRemoveSelectedWeekday(weekday)}
          className="spacing-m-2"
          isChecked={isSelected}
          size={IconSize.SIZE_ICON_16}
        />
        <Text className={`${styles.customMarginTopNegative4} ${isSelected ? 'content-primary' : ''}`} val={weekday.substring(0, 1)} size={TextSize.XXSMALL} bold />
      </div>
    );
  };

  return (
    <div className={`${styles.container} ${className} flex-row-align-center`}>
      {getDaySelector(Weekdays.MONDAY)}
      {getDaySelector(Weekdays.TUESDAY)}
      {getDaySelector(Weekdays.WEDNESDAY)}
      {getDaySelector(Weekdays.THURSDAY)}
      {getDaySelector(Weekdays.FRIDAY)}
      {getDaySelector(Weekdays.SATURDAY)}
      {getDaySelector(Weekdays.SUNDAY)}
    </div>
  );
}
